import { apiClient, ensureCsrfCookie } from '@/api/client'
import type { CandidateProfile } from '@/features/recruiter/api/candidates.api'
import type { ApiSuccessResponse, PaginationMeta } from '@/types/api'

export type ApplicationStatus = 'applied' | 'under_review' | 'shortlisted' | 'hired' | 'rejected' | 'withdrawn'

export interface Applicant {
  applicationId: number
  status: ApplicationStatus
  appliedAt: string
  recruiterNotes: string | null
  // Laravel's decimal:2 cast serializes to a JSON string, not a number.
  aiMatchScore: number | string | null
  candidate: CandidateProfile
}

async function withCsrf<T>(fn: () => Promise<T>): Promise<T> {
  await ensureCsrfCookie()
  return fn()
}

export const applicantsApi = {
  list: async (jobId: number, params: { status?: string; page?: number; per_page?: number }) => {
    const { data } = await apiClient.get<
      ApiSuccessResponse<Applicant[]> & { meta: PaginationMeta }
    >(`/recruiter/jobs/${jobId}/applicants`, { params })
    return { applicants: data.data, meta: data.meta }
  },

  get: async (applicationId: number) => {
    const { data } = await apiClient.get<ApiSuccessResponse<Applicant>>(
      `/recruiter/applications/${applicationId}`,
    )
    return data.data
  },

  downloadResume: async (applicationId: number) => {
    const { data } = await apiClient.get<ApiSuccessResponse<{ downloadUrl: string }>>(
      `/recruiter/applications/${applicationId}/resume`,
    )
    return data.data.downloadUrl
  },

  changeStatus: (applicationId: number, status: ApplicationStatus, notes?: string, offerLetter?: File) =>
    withCsrf(async () => {
      const url = `/recruiter/applications/${applicationId}/status`
      if (!offerLetter) {
        const { data } = await apiClient.put<ApiSuccessResponse<Applicant>>(url, { status, notes })
        return data.data
      }
      // PHP doesn't parse multipart PUT bodies — POST with Laravel's method override instead.
      const form = new FormData()
      form.append('_method', 'PUT')
      form.append('status', status)
      if (notes) form.append('notes', notes)
      form.append('offer_letter', offerLetter)
      const { data } = await apiClient.post<ApiSuccessResponse<Applicant>>(url, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return data.data
    }),
}
